/**
 * سربرگِ «عکس‌ها» — عکسِ رویدادها از کجا می‌آید و کجا خراب است (WHOSUP-163).
 *
 * ═══ چرا وجود دارد ═══
 *
 * عکسِ بیشترِ رویدادها آدرسی روی سایتِ منبع است، نه فایلی پیشِ ما. یعنی سه
 * جور خرابی دارد که هیچ تستی نمی‌گیرد: عکسی که در مبدأ پاک شده و روی دستگاهِ
 * کاربر مستطیلِ خالی می‌شود، بنرِ عمومی‌ای که یک منبع روی ده‌ها رویداد
 * گذاشته، و منبعی که اصلاً عکس نمی‌دهد. این صفحه هر سه را از یک RPC
 * می‌خواند (`admin_event_photos`، مایگریشن 0133).
 *
 * ═══ ستونِ «لود نشد» از کجا پر می‌شود ═══
 *
 * از خودِ دستگاه‌ها: `reportBrokenImage` (core/lib/report.ts) در هر دو اپ به
 * onErrorِ عکس‌ها بسته شده و هر آدرسِ http(s)ای را که لود نشود — هر آدرس در
 * هر نشست یک بار — به `client_errors` می‌فرستد. پس این فهرست از روزِ استقرارِ
 * همین تغییر به بعد پر می‌شود، نه عطفِ به ماسبق.
 *
 * ⚠ بندانگشتی‌ها از همین مرورگر لود می‌شوند؛ ✕ یعنی «الان از این‌جا هم
 * نیامد» — که برای فهرستِ خرابی‌ها خودش یک تاییدِ دستی است.
 *
 * ═══ چک‌باکسِ «معتبر» ═══
 *
 * کنارِ هر عکس یک چک‌باکسِ تیک‌خورده است. برداشتنِ تیک یعنی «این عکس معتبر
 * نیست، دیگر به کاربر نشانش نده» — و **نه** «پاکش کن»: آدرس سرِ جای خودش
 * روی ردیفِ رویداد می‌ماند و فقط یک پرچم روشن می‌شود (مایگریشن 0135). برای
 * همین تیکِ دوباره واقعاً برش می‌گرداند، همان لحظه.
 *
 * غیرمعتبرها در کارتِ «عکس‌های غیر معتبر» می‌مانند تا بشود برگرداندشان.
 *
 * کارتِ «چند عکسی» (`metadata.images`، WHOSUP-244) در `MultiPhotos.tsx` است.
 */
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminRepo } from '@/core/data/adminRepo';
import { opsRepo } from '@/core/data/opsRepo';
import { formatDateTime } from '@/core/lib/dates';
import { describeError, reportError } from '@/core/lib/report';
import { Hint } from '@/ui/Hint';
import { fmtNum as fmt } from '@/ui/Num';
import { MultiPhotos } from './MultiPhotos';
import { SuspectImages } from './SuspectImages';

/** دامنه‌ی درنیامده از آدرسِ بدشکل — به‌جای خالیِ مبهم. */
const hostLabel = (host: string | null): string => host ?? '—';

/** بندانگشتیِ عکس؛ اگر همین‌جا هم لود نشد، ✕ — که خودش یافته است. */
function Thumb({ url }: { url: string }) {
  const [bad, setBad] = useState(false);
  if (bad) return <span className="err" title="از این‌جا هم لود نشد">✕</span>;
  return (
    <img src={url} alt="" loading="lazy" onError={() => setBad(true)}
      style={{ width: 44, height: 32, objectFit: 'cover', borderRadius: 4 }} />
  );
}

/**
 * چک‌باکسِ «معتبر» — تیک یعنی به کاربر نشان داده می‌شود؛ برداشتنِ تیک یعنی
 * پنهانش کن (آدرس پاک نمی‌شود).
 *
 * وضعیت از جوابِ همان گزارش می‌آید و بعدِ هر تغییر کلِ گزارش تازه می‌شود —
 * چون یک تیک ردیف‌های چند فهرست را با هم جابه‌جا می‌کند (تکراری‌ها،
 * غیرمعتبرها، شمارش‌ها) و به‌روزکردنِ دستیِ تکه‌تکه‌شان دروغِ نصفه می‌سازد.
 */
function ValidBox({ url, invalid }: { url: string; invalid: boolean }) {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (valid: boolean) => adminRepo.setImageValid(url, valid),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['eventPhotos'] }),
    onError: (e) => reportError('admin.photos.valid', e, { url }),
  });
  return (
    <input type="checkbox" checked={!invalid} disabled={m.isPending}
      title={invalid
        ? 'غیر معتبر است — تیک بزن تا دوباره نشان داده شود'
        : 'معتبر است — تیک را بردار تا دیگر به کاربر نشان داده نشود (آدرسش پاک نمی‌شود)'}
      onChange={(e) => m.mutate(e.target.checked)} />
  );
}

/** آدرس، بریده ولی کامل در title و کلیک‌شدنی — آدرسِ عکس‌ها طولانی‌اند. */
function UrlCell({ url }: { url: string | null }) {
  if (!url) return <span className="dim">—</span>;
  const short = url.replace(/^https?:\/\//, '');
  return (
    <a className="mono ltr" href={url} target="_blank" rel="noreferrer" title={url}>
      {short.length > 64 ? `${short.slice(0, 64)}…` : short}
    </a>
  );
}

export function PhotosTab() {
  const q = useQuery({ queryKey: ['eventPhotos'], queryFn: () => opsRepo.eventPhotos() });

  if (q.isLoading) return <p className="dim">…</p>;
  // ⚠ نه String(q.error): خطای PostgREST یک شیءِ ساده است و String ازش
  // «[object Object]» می‌سازد — دقیقاً همان چیزی که یک بار روی همین صفحه
  // دیده شد، وقتی مایگریشن 0133 روی دیتابیس نبود و تابع پیدا نمی‌شد.
  if (q.error) return <div className="card"><p className="err">{describeError(q.error)}</p></div>;

  const d = q.data!;
  const t = d.totals;
  const share = (photos: number): string =>
    t.with_photo > 0 ? `${Math.round((photos / t.with_photo) * 100)}٪` : '—';

  return (
    <>
      <div className="grid n4" style={{ marginTop: 12 }}>
        <div className="card stat">
          <div className="n">{fmt(t.with_photo)}</div>
          <div className="k">
            <Hint text="رویدادِ زنده با عکس"
              help={`از ${fmt(t.live_events)} رویدادِ زنده (published و merge‌نشده). ${fmt(t.excluded)} ردیفِ دیگر (rejected/merged/…) شمرده نشده.`
                + (t.hidden > 0
                  ? ` از این‌ها ${fmt(t.hidden)} تا «غیر معتبر»اند و به کاربر نشان داده نمی‌شوند.`
                  : '')} />
          </div>
        </div>
        <div className="card stat">
          <div className="n">{fmt(t.live_events - t.with_photo)}</div>
          <div className="k">بی‌عکس</div>
        </div>
        <div className="card stat">
          <div className="n">{fmt(t.dup_urls)}</div>
          <div className="k">
            <Hint text="عکسِ تکراری"
              help={`آدرس‌هایی که بیش از یک رویدادِ زنده استفاده‌شان می‌کند — روی‌هم ${fmt(t.dup_events)} رویداد.`} />
          </div>
        </div>
        <div className="card stat">
          <div className="n">{fmt(t.failed_urls)}</div>
          <div className="k">
            <Hint text="روی دستگاهِ کاربر لود نشد"
              help={`آدرسِ یکتا؛ روی‌هم ${fmt(t.failure_reports)} گزارش از دستگاه‌ها.`} />
          </div>
        </div>
      </div>

      {/* ═══ لود نشده‌ها اول: تنها فهرستی که کاربر واقعاً باهاش مواجه شده ═══ */}
      <div className="card">
        <h2>روی دستگاهِ کاربر لود نشد</h2>
        {d.failures.length === 0 ? (
          <p className="dim" style={{ marginTop: 10 }}>
            هنوز گزارشی نرسیده. هر دو اپ از استقرارِ WHOSUP-163 به بعد هر عکسی
            را که روی دستگاه لود نشود ثبت می‌کنند (هر آدرس، در هر نشست یک بار) —
            پس خالی بودنِ این فهرست یا یعنی واقعاً همه‌چیز لود می‌شود، یا یعنی
            نسخه‌ی جدید هنوز دستِ کسی نیست.
          </p>
        ) : (
          <div className="scrollx">
            <table className="tbl">
              <thead>
                <tr>
                  <th><Hint text="معتبر" help="برداشتنِ تیک = این عکس دیگر به کاربر نشان داده نشود. آدرسش پاک نمی‌شود و هر وقت خواستی برمی‌گردد." /></th>
                  <th>عکس</th><th>آدرس</th><th>گزارش</th>
                  <th><Hint text="کاربر" help="کاربرِ واردشده‌ی یکتا؛ مهمان‌ها فقط در شمارِ گزارش‌اند." /></th>
                  <th>آخرین بار</th>
                  <th><Hint text="هنوز روی" help="چند رویدادِ زنده هنوز همین آدرس را دارند. صفر یعنی عکس از آن موقع عوض شده." /></th>
                  <th>نمونه</th>
                </tr>
              </thead>
              <tbody>
                {d.failures.map((f) => (
                  <tr key={f.url}>
                    <td><ValidBox url={f.url} invalid={f.invalid} /></td>
                    <td><Thumb url={f.url} /></td>
                    <td><UrlCell url={f.url} /></td>
                    <td className="num">{fmt(f.reports)}</td>
                    <td className="num">{fmt(f.users)}</td>
                    <td className="num">{formatDateTime(f.last_at)}</td>
                    <td className="num">{f.live_events === 0
                      ? <span className="chip">رفع شده</span> : fmt(f.live_events)}</td>
                    <td>{f.title ?? <span className="dim">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* غیرمعتبرها فقط وقتی چیزی هست — و بلافاصله بعدِ خرابی‌ها، که خاستگاهِ
          بیشترِ علامت‌زدن‌هاست؛ ردیفِ غیرمعتبر از فهرست‌های دیگر نمی‌افتد ولی
          این‌جا یکجا دیده می‌شود و برگرداندنش یک تیک است. */}
      {d.invalid.length > 0 ? (
        <div className="card">
          <h2>
            <Hint text="عکس‌های غیر معتبر"
              help="به کاربر نشان داده نمی‌شوند، ولی آدرسشان روی رویداد مانده و کرالر هم مثل قبل می‌بیندشان. تیک زدن همان لحظه برشان می‌گرداند." />
          </h2>
          <div className="scrollx">
            <table className="tbl">
              <thead>
                <tr>
                  <th>معتبر</th><th>عکس</th><th>آدرس</th><th>از کِی</th>
                  <th><Hint text="روی چند رویداد" help="آدرس سرِ جایش مانده، پس این عدد صفر نیست — همین‌قدر رویداد با تیکِ دوباره عکسشان برمی‌گردد." /></th>
                  <th><Hint text="هنوز دیده می‌شود" help="باید صفر باشد. غیرِ صفر یعنی پرچمِ یک ردیف جا مانده و آن رویداد هنوز این عکس را نشان می‌دهد." /></th>
                </tr>
              </thead>
              <tbody>
                {d.invalid.map((b) => (
                  <tr key={b.url}>
                    <td><ValidBox url={b.url} invalid /></td>
                    <td><Thumb url={b.url} /></td>
                    <td><UrlCell url={b.url} /></td>
                    <td className="num">{formatDateTime(b.marked_at)}</td>
                    <td className="num">{fmt(b.on_events)}</td>
                    <td className="num">{b.shown === 0
                      ? <span className="dim">۰</span>
                      : <span className="err">{fmt(b.shown)}</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      <SuspectImages />

      <MultiPhotos />

      <div className="card">
        <h2><Hint text="عکس‌ها از کجا می‌آیند" help="دامنه‌ی خودِ آدرسِ عکس، نه منبعِ رویداد." /></h2>
        {d.by_host.length === 0 ? (
          <p className="dim" style={{ marginTop: 10 }}>هیچ رویدادِ زنده‌ای عکس ندارد.</p>
        ) : (
          <div className="scrollx">
            <table className="tbl">
              <thead>
                <tr>
                  <th>دامنه</th><th>عکس</th><th>سهم</th>
                  <th><Hint text="آدرسِ یکتا" help="چند فایلِ متفاوت — فاصله‌ی این با ستونِ «عکس» یعنی تکرار." /></th>
                  <th>آدرسِ تکراری</th><th>بیشترین تکرار</th>
                </tr>
              </thead>
              <tbody>
                {d.by_host.map((h) => (
                  <tr key={h.host ?? '~'}>
                    <td className="mono ltr">{hostLabel(h.host)}</td>
                    <td className="num">{fmt(h.photos)}</td>
                    <td className="num">{share(h.photos)}</td>
                    <td className="num">{fmt(h.urls)}</td>
                    <td className="num">{h.dup_urls > 0 ? fmt(h.dup_urls) : <span className="dim">۰</span>}</td>
                    <td className="num">{h.max_repeat >= 2 ? fmt(h.max_repeat) : <span className="dim">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <h2><Hint text="به تفکیکِ منبع" help="ستونِ events.source — منبعِ کرالر، یا manual/user_submitted." /></h2>
        <div className="scrollx">
          <table className="tbl">
            <thead>
              <tr>
                <th>منبع</th><th>رویداد</th><th>با عکس</th><th>بی‌عکس</th>
                <th>آدرسِ تکراری</th>
                <th><Hint text="رویدادِ درگیر" help="جمعِ رویدادهایی که عکسِ مشترک دارند." /></th>
                <th>بیشترین تکرار</th><th>پرتکرارترین آدرس</th>
              </tr>
            </thead>
            <tbody>
              {d.by_source.map((s) => (
                <tr key={s.source}>
                  <td className="mono ltr">{s.source}</td>
                  <td className="num">{fmt(s.events)}</td>
                  <td className="num">{fmt(s.with_photo)}</td>
                  <td className="num">{s.events - s.with_photo > 0
                    ? fmt(s.events - s.with_photo) : <span className="dim">۰</span>}</td>
                  <td className="num">{s.dup_urls > 0 ? fmt(s.dup_urls) : <span className="dim">۰</span>}</td>
                  <td className="num">{s.dup_events > 0 ? fmt(s.dup_events) : <span className="dim">۰</span>}</td>
                  <td className="num">{s.max_repeat >= 2 ? fmt(s.max_repeat) : <span className="dim">—</span>}</td>
                  <td><UrlCell url={s.top_url} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <h2>تکراری‌ترین عکس‌ها</h2>
        {d.top_dups.length === 0 ? (
          <p className="dim" style={{ marginTop: 10 }}>هیچ عکسی روی بیش از یک رویدادِ زنده ننشسته.</p>
        ) : (
          <div className="scrollx">
            <table className="tbl">
              <thead>
                <tr>
                  <th><Hint text="مجاز" help="برداشتنِ تیک = این عکس دیگر استفاده نشود؛ همه‌ی این رویدادها بی‌عکس می‌شوند." /></th>
                  <th>عکس</th><th>آدرس</th><th>تکرار</th>
                  <th><Hint text="منبع" help="چند منبعِ متفاوت — بیش از یکی یعنی تکرار فقط تقصیرِ یک سایت نیست." /></th>
                  <th>نمونه</th>
                </tr>
              </thead>
              <tbody>
                {d.top_dups.map((u) => (
                  <tr key={u.url}>
                    <td><ValidBox url={u.url} invalid={u.invalid} /></td>
                    <td><Thumb url={u.url} /></td>
                    <td><UrlCell url={u.url} /></td>
                    <td className="num">{fmt(u.uses)}</td>
                    <td className="num">{fmt(u.sources)}</td>
                    <td>{u.titles.join(' · ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
